/*
  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import isObject from "lodash/isObject";
import React, { useEffect, useState } from "react";
import { SketchPicker } from "react-color";
import ButtonBase from "@mui/material/ButtonBase";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Popover from "@mui/material/Popover";
import makeStyles from "@mui/styles/makeStyles";
import { useTheme } from "@mui/material/styles";

import { rgbToHex } from "@marapp/earth-shared";

const useStyles = makeStyles((theme) => ({
  root: {
    height: theme.spacing(7),
  },
  colorPreview: {
    borderRadius: 4,
    height: theme.spacing(7),
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    width: "100%",
    minWidth: theme.spacing(7),
    "& > div": {
      borderRadius: 4,
      height: "100%",
      width: "100%",
    },
  },
  // Restyle react-color's SketchPicker (which is white by default) to match
  // the site's dark theme.
  sketchWrapper: {
    "& input": {
      backgroundColor: `${theme.palette.background.default} !important`,
      color: `${theme.palette.text.primary} !important`,
      boxShadow: `${theme.palette.divider} 0 0 0 1px inset !important`,
    },
    "& span, & label": {
      color: `${theme.palette.text.primary} !important`,
    },
  },
}));

export interface Color {
  r?: string;
  g?: string;
  b?: string;
  a?: string;
}

export interface ColorInputProps {
  disabled?: boolean;
  className?: string;
  label?: string;
  // "string" emits a hex colour — 8-digit `#RRGGBBAA`, or 6-digit `#RRGGBB`
  // when `disableOpacity` makes the alpha pair a constant `ff`.
  outputType?: "object" | "string";
  disablePortal?: boolean;
  disableOpacity?: boolean;
  onChange?: (value: any) => void;
  [key: string]: any;
}

const getBackgroundColor = (color: Color) => {
  return isObject(color)
    ? `rgba(${color.r},${color.g},${color.b},${color.a})`
    : color;
};

export const ColorInput = React.forwardRef(
  (props: ColorInputProps, ref: any) => {
    const {
      label,
      className,
      disabled,
      outputType = "object",
      disablePortal = false,
      disableOpacity = false,
      onChange,
    } = props;
    const classes = useStyles();
    const theme = useTheme();

    const [anchorEl, setAnchorEl] = useState(null);

    // Local colour driving the picker and preview, so they still follow the drag
    // if a caller doesn't feed props.value back. Re-synced when it changes.
    const [localColor, setLocalColor] = useState(props.value);
    useEffect(() => {
      setLocalColor((prev) =>
        getBackgroundColor(prev) === getBackgroundColor(props.value) ? prev : props.value
      );
    }, [props.value]);

    const openColorPicker = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const toOutput = (value) => {
      // If opacity is disabled, ensure alpha is always 1 (fully opaque)
      const rgb = value?.rgb || value;
      const alpha = disableOpacity ? 1 : rgb?.a ?? 1;
      if (outputType === "object") {
        return { ...rgb, a: alpha };
      }
      const hex = rgbToHex([rgb.r, rgb.g, rgb.b, alpha]);
      // A pinned alpha carries no information, and consumers like Mapbox GL and
      // GEE's SLD only take 6-digit hex — so drop the pair rather than emit `ff`.
      return disableOpacity ? hex.slice(0, 7) : hex;
    };

    const handleColorChange = (value) => {
      const output = toOutput(value);
      setLocalColor(output);
      onChange?.(output);
    };

    const closeColorPicker = () => {
      setAnchorEl(null);
    };

    useEffect(() => {
      if (props.defaultValue && !props.value) {
        const output = toOutput(props.defaultValue);
        setLocalColor(output);
        onChange?.(output);
      }
    }, []);

    return (
      <>
        <FormControl
          className={classes.root}
          fullWidth={true}
          variant="outlined"
        >
          <InputLabel shrink={!!localColor}>{label}</InputLabel>

          <div className={`${className} ${classes.colorPreview}`}>
            <ButtonBase
              component="div"
              style={{ backgroundColor: getBackgroundColor(localColor) }}
              disabled={disabled}
              onClick={openColorPicker}
            />

            <Popover
              open={!!anchorEl}
              disableEnforceFocus={true}
              anchorEl={anchorEl}
              onClose={closeColorPicker}
              disablePortal={disablePortal}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
            >
              <div className={classes.sketchWrapper}>
                <SketchPicker
                  color={localColor}
                  onChange={(color) => handleColorChange(color)}
                  disableAlpha={disableOpacity}
                  styles={{
                    default: {
                      picker: {
                        background: theme.palette.grey[700],
                        boxShadow: `0 0 0 1px ${theme.palette.divider}, 0 8px 16px rgba(0,0,0,.4)`,
                      },
                    },
                  }}
                />
              </div>
            </Popover>
          </div>
        </FormControl>
      </>
    );
  }
);
